import { useId, useRef, useState } from 'react';

import {
  createImageObject,
  IMAGE_UPLOAD_MAX_FILE_SIZE,
  MAX_IMAGE_DIMENSION_SIZE,
  uploadImageToS3,
} from '@api/instant_survey';
import type { Concept, QuestionImage } from '@api/instant_survey/types';

import {
  Button,
  CloseIcon,
  IconButton,
  ImageIcon,
  Input,
  ModalV2 as Modal,
  toast,
  validateImageDimensions,
} from '@utilities';

import { getConceptImageUrl } from '../../utilities/helpers';

import styles from './_index.module.scss';

export interface ConceptDraft {
  conceptImage: null | QuestionImage;
  conceptName: string;
}

interface CommonModalAddConceptProps {
  onClose: () => void;
}

interface CreateModalAddConceptProps extends CommonModalAddConceptProps {
  concept?: never;
  mode: 'create';
  onCreate: (draft: ConceptDraft) => void;
  onUpdate?: never;
}

interface EditModalAddConceptProps extends CommonModalAddConceptProps {
  concept: Concept;
  mode: 'edit';
  onCreate?: never;
  onUpdate: (draft: ConceptDraft) => void;
}

type ModalAddConceptProps = CreateModalAddConceptProps | EditModalAddConceptProps;

const ModalAddConcept: React.FC<ModalAddConceptProps> = ({
  concept,
  mode,
  onClose,
  onCreate,
  onUpdate,
}: ModalAddConceptProps) => {
  const addConceptInputId = useId();
  const conceptNameInputId = useId();
  const conceptImageInputRef = useRef<HTMLInputElement>(null);
  const [conceptImage, setConceptImage] = useState<null | QuestionImage>(concept?.image ?? null);
  const [conceptName, setConceptName] = useState<string>(concept?.name ?? '');
  const [isUploadingConcept, setIsUploadingConcept] = useState<boolean>(false);

  const isEditing = mode === 'edit';
  const conceptImageUrl = getConceptImageUrl(conceptImage);
  const isSubmitDisabled = !conceptName.trim() || isUploadingConcept;

  const handleClose = () => {
    if (isUploadingConcept) return;
    onClose();
  };

  const handleConceptSubmit = () => {
    if (isSubmitDisabled) return;

    const draft = { conceptImage, conceptName: conceptName.trim() };

    if (isEditing) {
      onUpdate(draft);
      return;
    }

    onCreate(draft);
  };

  const handleConceptImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (!file) return;

    if (file.size > IMAGE_UPLOAD_MAX_FILE_SIZE) {
      toast.error('File size exceeds 5MB limit');
      input.value = '';
      return;
    }

    try {
      await validateImageDimensions({ file, maxDimensionSize: MAX_IMAGE_DIMENSION_SIZE });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to read image dimensions');
      input.value = '';
      return;
    }

    setIsUploadingConcept(true);

    try {
      const imageObject = await createImageObject({ objectType: file.type.substring(6) });

      if (!imageObject.url.startsWith('http://s3mock')) {
        await uploadImageToS3({
          fields: imageObject.fields,
          file,
          url: imageObject.url,
        });
      }

      setConceptImage({
        id: imageObject.id,
        previewUrl: URL.createObjectURL(file),
      });
    } catch (error: any) {
      toast.error('Error uploading concept image');
      console.error('Error uploading concept image:', error);
    } finally {
      setIsUploadingConcept(false);
      input.value = '';
    }
  };

  const handleConceptImageRemove = () => {
    if (isUploadingConcept) return;
    setConceptImage(null);
  };

  const handleConceptImageDropzoneKeyDown = (event: React.KeyboardEvent<HTMLLabelElement>) => {
    if (isUploadingConcept || (event.key !== 'Enter' && event.key !== ' ')) return;

    event.preventDefault();
    conceptImageInputRef.current?.click();
  };

  return (
    <Modal
      buttons={[
        <Button key="cancel" onClick={handleClose} text="Cancel" variant="secondary" />,
        <Button
          isDisabled={isSubmitDisabled}
          key="submit"
          onClick={handleConceptSubmit}
          text={isEditing ? 'Update' : 'Create'}
        />,
      ]}
      isActive
      onClose={handleClose}
      size="large"
      title={isEditing ? 'Edit Concept' : 'Add Concept'}
    >
      <div className={styles['add-concept-modal-content']}>
        <Input
          autoFocus
          className={styles['add-concept-name-input']}
          id={conceptNameInputId}
          isDisabled={isUploadingConcept}
          isRequired
          label="CONCEPT NAME"
          maxLength={128}
          onChange={(value) => setConceptName(String(value))}
          placeholder="Enter text"
          value={conceptName}
        />

        {/* the remove button is a sibling of the label, not a child: interactive content is not
            allowed inside a label, and nesting it would re-open the file picker on every click */}
        <div className={styles['add-concept-image-container']}>
          <label
            aria-disabled={isUploadingConcept}
            aria-label={conceptImageUrl ? 'Change concept image' : 'Add concept image'}
            className={styles['add-concept-image-dropzone']}
            htmlFor={addConceptInputId}
            onKeyDown={handleConceptImageDropzoneKeyDown}
            role="button"
            tabIndex={isUploadingConcept ? -1 : 0}
          >
            {conceptImageUrl ? (
              <img
                alt={conceptName || 'Concept image'}
                className={styles['add-concept-image-preview']}
                src={conceptImageUrl}
              />
            ) : (
              <div className={styles['add-concept-image-empty']}>
                <ImageIcon />
                <span className={styles['add-concept-image-button']}>
                  {isUploadingConcept ? 'Uploading...' : 'Add Image'}
                </span>
              </div>
            )}
          </label>
          {conceptImageUrl && !isUploadingConcept && (
            <IconButton
              aria-label="Remove image"
              className={styles['add-concept-remove-image-button']}
              onClick={handleConceptImageRemove}
              tooltip="Remove Image"
              type="button"
            >
              <CloseIcon />
            </IconButton>
          )}
        </div>
        <input
          accept="image/gif, image/jpeg, image/png"
          aria-hidden
          className={styles['add-concept-input']}
          data-testid="add-concept-image-input"
          disabled={isUploadingConcept}
          id={addConceptInputId}
          onChange={handleConceptImageUpload}
          ref={conceptImageInputRef}
          type="file"
        />
      </div>
    </Modal>
  );
};

export default ModalAddConcept;
